'use client';

import { Loader2 } from 'lucide-react';
import { signIn, useSession } from 'next-auth/react';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import posthog, { BEACON_OPTIONS } from '@/lib/posthog';
import { getFrontUrl } from '@/lib/auth/frontUrl';
import useUserContextStore from '@/contexts/UserContext';

export type Providers =
  'github' | 'gitlab' | 'gitlab-self-hosted' | 'bitbucket' | 'google';

export type ButtonProviderProps = {
  provider: Providers;
  isLoading?: boolean;
  redirectUri?: string | null;
  isSignIn?: boolean;
};

function ProviderIcon({ provider }: { provider: Providers }) {
  if (provider === 'github') {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10 0C4.477 0 0 4.477 0 10c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.604-3.369-1.341-3.369-1.341-.454-1.154-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 10 4.836a9.59 9.59 0 0 1 2.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C17.137 18.163 20 14.418 20 10c0-5.523-4.477-10-10-10Z"
          fill="#24292F"
        />
      </svg>
    );
  }
  if (provider.match(/gitlab/)) {
    return (
      <Image
        src="/assets/gitlab.svg"
        alt="GitLab"
        width={20}
        height={20}
        className="shrink-0"
      />
    );
  }
  if (provider === 'bitbucket') {
    return (
      <Image
        src="/assets/bitbucket.svg"
        alt="Bitbucket"
        width={20}
        height={20}
        className="shrink-0"
      />
    );
  }
  if (provider === 'google') {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <path
          d="M19.6 10.23c0-.68-.06-1.36-.18-2.02H10v3.82h5.4a4.62 4.62 0 0 1-2 3.03v2.52h3.24c1.9-1.75 2.96-4.33 2.96-7.35Z"
          fill="#4285F4"
        />
        <path
          d="M10 20c2.7 0 4.97-.9 6.63-2.42l-3.24-2.52c-.9.6-2.06.96-3.39.96-2.6 0-4.81-1.76-5.6-4.13H1.06v2.6A10 10 0 0 0 10 20Z"
          fill="#34A853"
        />
        <path
          d="M4.4 11.89a6 6 0 0 1 0-3.78V5.51H1.06a10 10 0 0 0 0 8.98l3.34-2.6Z"
          fill="#FBBC05"
        />
        <path
          d="M10 3.98c1.47 0 2.79.5 3.82 1.5l2.87-2.87A10 10 0 0 0 1.06 5.51l3.34 2.6C5.19 5.75 7.4 3.98 10 3.98Z"
          fill="#EA4335"
        />
      </svg>
    );
  }
  return null;
}

export const ButtonProviders = ({
  provider,
  isLoading: loading = false,
  redirectUri,
  isSignIn,
}: ButtonProviderProps) => {
  const { status } = useSession();
  const [isLoading, setLoading] = useState(loading);
  const router = useRouter();

  const signInText = isSignIn
    ? {
        github: 'Sign in with Github',
        gitlab: 'Sign in with Gitlab',
        'gitlab-self-hosted': 'Sign in with Self-Hosted Gitlab',
        bitbucket: 'Sign in with Bitbucket',
        google: 'Sign in with Google',
      }
    : {
        github: 'Signup with Github',
        gitlab: 'Signup with Gitlab',
        'gitlab-self-hosted': 'Signup with Self-Hosted Gitlab',
        bitbucket: 'Signup with Bitbucket',
        google: 'Signup with Google',
      };

  const handleSignIn = async ({ provider }: ButtonProviderProps) => {
    const startTime = Date.now();
    const eventName = isSignIn ? 'login_started' : 'signup_started';

    // Track start of auth flow. Sent instantly over sendBeacon: every branch
    // below navigates away, and a batched event would be lost with the page.
    posthog.capture(
      eventName,
      {
        provider,
        referrer: document.referrer,
        has_invite: new URLSearchParams(window.location.search).has('invite'),
        has_existing_session: status === 'authenticated',
      },
      BEACON_OPTIONS
    );

    setLoading(true);
    try {
      // Every branch below navigates away; give the analytics SDK a moment to
      // take the event above so it isn't dropped with the page.
      await posthog.ready();
      if (provider === 'gitlab-self-hosted') {
        localStorage.setItem('isSignUp', 'true');
        // Collect all current query params
        const search = window.location.search;
        // If isSignIn is false, ensure isSignUp=true is present in params
        let nextSearch = search;
        if (!isSignIn) {
          const params = new URLSearchParams(search);
          params.set('isSignUp', 'true');
          nextSearch = '?' + params.toString();
        }
        router.push(`auth/self-hosted${nextSearch}`);
        return;
      }

      if (redirectUri && redirectUri.length > 0) {
        const decodedUri = redirectUri;
        const isAbsoluteUrl = decodedUri.startsWith('http');
        const frontUrl = getFrontUrl();
        const result = await signIn(provider, {
          redirect: true,
          callbackUrl: isAbsoluteUrl ? decodedUri : `${frontUrl}${decodedUri}`,
        });

        // Track completion
        const completionEventName = isSignIn
          ? 'login_completed'
          : 'signup_completed';
        posthog.capture(completionEventName, {
          provider,
          time_to_complete: Date.now() - startTime,
          success: true,
        });

        return result;
      }

      const result = await signIn(provider, {
        redirect: true,
        callbackUrl: '/home',
      });

      // Track completion
      const completionEventName = isSignIn
        ? 'login_completed'
        : 'signup_completed';
      posthog.capture(completionEventName, {
        provider,
        time_to_complete: Date.now() - startTime,
        success: true,
      });

      return result;
    } catch (error) {
      console.error('Sign in error:', error);

      // Track error
      const errorEventName = isSignIn ? 'login_error' : 'signup_error';
      posthog.capture(errorEventName, {
        provider,
        error_type: (error as Error).name || 'Unknown',
        error_message: (error as Error).message || 'An unknown error occurred',
        time_to_error: Date.now() - startTime,
      });

      setLoading(false);
    }
  };

  return (
    <button
      className="font-instrument-sans text-sm font-medium w-full h-9 bg-ds-surface-white hover:bg-ds-gray-subtlest border-[1.5px] border-ds-gray-subtlest rounded-lg px-2 text-ds-text-default transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
      onClick={() => handleSignIn({ provider })}
      disabled={isLoading}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <>
          <ProviderIcon provider={provider} />
          <span className="text-ds-text-default text-sm font-medium">
            {signInText[provider]}
          </span>
        </>
      )}
    </button>
  );
};

export const mountScope = (provider: string) => {
  const scopes = {
    // Keep in sync with the GitHub provider scope in
    // src/app/api/auth/[...nextauth]/providers.ts
    github: 'read:user user:email read:org',
    gitlab:
      'api read_api read_user read_repository profile email write_repository',
    bitbucket:
      'account email project repository pullrequest pullrequest:write webhook pipeline',
    google: 'openid email profile',
  };
  return (
    scopes[provider as keyof typeof scopes] ||
    'api read_api read_user read_repository profile email write_repository'
  );
};

/**
 * Routing map for CLI and extension name parameters
 * Generates appropriate callbackUrl based on URL search parameters
 */
export const getAuthCallbackUrl = (
  searchParams: URLSearchParams | null,
  defaultCallbackUrl?: string
): string => {
  if (!searchParams) {
    return defaultCallbackUrl || '/signup?callbackUrl=/onboarding&loading=true';
  }
  const orguuid = useUserContextStore.getState().user?.OrganizationUUID;
  const extensionName = searchParams.get('extensionName');
  const cli = searchParams.get('cli');

  // Routing map: priority order matters
  if (extensionName) {
    return `/extension/login?extensionName=${extensionName}`;
  }

  if (cli === 'true' && orguuid === '') {
    return '/onboarding';
  } else if (cli === 'true' && orguuid !== null) {
    return '/settings?tab=api';
  }

  // Use provided callbackUrl or default
  const callbackUrl = searchParams.get('callbackUrl') || defaultCallbackUrl;
  if (callbackUrl) {
    return callbackUrl;
  }

  return '/signup?callbackUrl=/onboarding&loading=true';
};
